import { format, parse, parseISO } from 'date-fns'
import { es } from 'date-fns/locale/es'

/**
 * "Jue 1 oct" para el pie de la tarjeta de RASTRO (§8.2), y "Jue 1 oct ·
 * 18:00" si tiene hora (DEC-20).
 */
export function formatearDiaTarjeta(fecha: string, hora?: string | null): string {
  const d = parseISO(fecha)
  const texto = format(d, 'EEE d MMM', { locale: es })
  const dia = texto.charAt(0).toUpperCase() + texto.slice(1)
  return hora ? `${dia} · ${hora}` : dia
}

/** "Octubre 2026" para el navegador de mes del encabezado. */
export function formatearMes(mes: string): string {
  const d = parse(`${mes}-01`, 'yyyy-MM-dd', new Date())
  const texto = format(d, 'MMMM yyyy', { locale: es })
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

/** "15 de octubre de 2026", para confirmaciones y avisos. Con hora: "… · 18:00 h". */
export function formatearFechaLarga(fecha: string, hora?: string | null): string {
  const dia = format(parseISO(fecha), "d 'de' MMMM 'de' yyyy", { locale: es })
  return hora ? `${dia} · ${hora} h` : dia
}

export const mesDeFecha = (fecha: string) => fecha.slice(0, 7)

export const mesActual = () => format(new Date(), 'yyyy-MM')

/** Mes anterior o siguiente, en formato "AAAA-MM". */
export function correrMes(mes: string, pasos: number): string {
  const d = parse(`${mes}-01`, 'yyyy-MM-dd', new Date())
  d.setMonth(d.getMonth() + pasos)
  return format(d, 'yyyy-MM')
}

/* ── Calendario (§8.3) ──────────────────────────────────── */

/** Hoy en Buenos Aires, "AAAA-MM-DD". La fecha pautada no depende del reloj. */
export function hoyEnBuenosAires(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/**
 * Los 42 días de la cuadrícula de un mes, de lunes a domingo. Siempre seis
 * semanas: así la grilla no cambia de alto al pasar de mes y el arrastre no
 * persigue un blanco que se mueve.
 */
export function diasDelMes(mes: string): string[] {
  const primero = parse(`${mes}-01`, 'yyyy-MM-dd', new Date())
  // getDay(): domingo = 0. La semana empieza el lunes.
  const corrimiento = (primero.getDay() + 6) % 7
  const inicio = new Date(primero)
  inicio.setDate(inicio.getDate() - corrimiento)

  const dias: string[] = []
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(inicio)
    d.setDate(d.getDate() + i)
    dias.push(format(d, 'yyyy-MM-dd'))
  }
  return dias
}

/** "AAAA-MM-DD..AAAA-MM-DD" de la cuadrícula, para pedirle las piezas al servidor. */
export function rangoDelMes(mes: string): string {
  const dias = diasDelMes(mes)
  return `${dias[0] ?? ''}..${dias[41] ?? ''}`
}

/** "Jueves 15" para el encabezado de un día en la agenda del celular. */
export function formatearDiaAgenda(fecha: string): string {
  const texto = format(parseISO(fecha), 'EEEE d', { locale: es })
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

/** "24 de octubre", para el aviso de reprogramada. */
export const formatearDiaYMes = (fecha: string) =>
  format(parseISO(fecha), "d 'de' MMMM", { locale: es })

/** Los siete días de la semana que contiene esa fecha, de lunes a domingo. */
export function semanaDe(fecha: string): string[] {
  const d = parseISO(fecha)
  const corrimiento = (d.getDay() + 6) % 7
  const lunes = new Date(d)
  lunes.setDate(lunes.getDate() - corrimiento)
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(lunes)
    x.setDate(x.getDate() + i)
    return format(x, 'yyyy-MM-dd')
  })
}

/** Iniciales de la tira de la semana en celular: L M X J V S D. */
export const INICIALES_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const

/** Nombres de las columnas en escritorio. */
export const COLUMNAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const

/** "1 oct al 31 oct 2026" (el año una sola vez si coincide), para una planificación. */
export function formatearPeriodo(desde: string, hasta: string): string {
  const d = parseISO(desde)
  const h = parseISO(hasta)
  const mismoAnio = d.getFullYear() === h.getFullYear()
  return `${format(d, mismoAnio ? 'd MMM' : 'd MMM yyyy', { locale: es })} al ${format(h, 'd MMM yyyy', { locale: es })}`
}
